/**
 * Tracks API reachability (503 / network outages) so the UI can show one calm
 * banner instead of a stack of red toasts.
 *
 * A single blip does not mark a full outage — require consecutive failures
 * (or a confirming failed health probe) first.
 */
import { serverURL } from '../constants';

const EVENT = 'coursolo:connectivity';
const POLL_MS = 12_000;
/** Consecutive outage-class failures before showing the banner. */
const FAILURES_BEFORE_OUTAGE = 2;

let outage = false;
let pollTimer = null;
let lastReason = null;
let consecutiveFailures = 0;
let probeInFlight = false;

function emit(status, detail = {}) {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent(EVENT, {
        detail: { status, online: !outage, apiOutage: outage, ...detail },
      })
    );
  } catch {
    /* ignore */
  }
}

function stopPoll() {
  if (pollTimer != null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function markOutage(reason) {
  if (outage) return;
  outage = true;
  lastReason = reason;
  emit('api-down', { reason });
  startPoll();
}

async function probeHealth() {
  try {
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = setTimeout(() => ctrl?.abort(), 8000);
    const res = await fetch(`${serverURL}/api/health`, {
      method: 'GET',
      cache: 'no-store',
      signal: ctrl?.signal,
    });
    clearTimeout(timer);
    if (res.ok) {
      consecutiveFailures = 0;
      clearApiOutage();
      return true;
    }
  } catch {
    /* still down */
  }
  return false;
}

function confirmWithHealthProbe(reason) {
  if (probeInFlight || typeof window === 'undefined') return;
  probeInFlight = true;
  probeHealth()
    .then((ok) => {
      if (!ok && consecutiveFailures >= 1) {
        consecutiveFailures = Math.max(consecutiveFailures, FAILURES_BEFORE_OUTAGE);
        markOutage(reason || lastReason || 'unavailable');
      }
    })
    .finally(() => {
      probeInFlight = false;
    });
}

function startPoll() {
  if (pollTimer != null || typeof window === 'undefined') return;
  pollTimer = setInterval(() => {
    probeHealth();
  }, POLL_MS);
}

export function isApiOutage() {
  return outage;
}

export function getApiOutageReason() {
  return lastReason;
}

/** True for gateway / network failures that should not spam red toasts. */
export function isApiOutageError(error) {
  if (!error || error.cancelled || error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
    return false;
  }
  const status = error.response?.status;
  if (status === 502 || status === 504) return true;
  if (status === 503) {
    // App-level 503s (feature off / not configured) are not infrastructure outages.
    const msg = String(error.response?.data?.message || error.message || '').toLowerCase();
    if (
      /not configured|temporarily disabled|tts unavailable|sharing is|stripe not|email is not/i.test(msg)
    ) {
      return false;
    }
    return true;
  }
  if (!error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error')) {
    return true;
  }
  return false;
}

/**
 * Mark API as down. By default requires FAILURES_BEFORE_OUTAGE consecutive
 * reports (or a failed health probe). Pass `{ force: true }` to skip the threshold.
 */
export function reportApiOutage(reason = 'unavailable', { force = false } = {}) {
  lastReason = reason;
  if (force) {
    consecutiveFailures = FAILURES_BEFORE_OUTAGE;
    markOutage(reason);
    return;
  }

  consecutiveFailures += 1;
  if (consecutiveFailures >= FAILURES_BEFORE_OUTAGE) {
    markOutage(reason);
    return;
  }

  // First blip: confirm with a health probe before raising the banner.
  confirmWithHealthProbe(reason);
}

export function clearApiOutage() {
  consecutiveFailures = 0;
  if (!outage) return;
  outage = false;
  lastReason = null;
  stopPoll();
  emit('api-up');
}

/** Call on any successful API response while recovering. */
export function noteApiSuccess() {
  consecutiveFailures = 0;
  if (outage) clearApiOutage();
}

export function reportErrorIfOutage(error) {
  if (!isApiOutageError(error)) return false;
  const status = error.response?.status;
  reportApiOutage(status ? `http-${status}` : 'network');
  return true;
}

/** Test helper — reset module state between Jest cases. */
export function __resetApiAvailabilityForTests() {
  outage = false;
  lastReason = null;
  consecutiveFailures = 0;
  probeInFlight = false;
  stopPoll();
}

const apiAvailability = {
  isApiOutage,
  isApiOutageError,
  reportApiOutage,
  clearApiOutage,
  noteApiSuccess,
  reportErrorIfOutage,
};

export default apiAvailability;
